import type { FC } from 'react';
import { Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_PROFILE_SECTION_PATHS, Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import { useDispatch } from 'react-redux';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import ShareIcon from '@wix/da-ds/pkg/Icons/24x24/Share';
import InfoIcon from '@wix/da-ds/pkg/Icons/24x24/Info';
import QuillIcon from '@wix/da-ds/pkg/Icons/24x24/Quill';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import BlockIcon from '@wix/da-ds/pkg/Icons/24x24/Block';
import TierIcon from '@wix/da-ds/pkg/Icons/24x24/Tier';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import ErrorIcon from '@wix/da-ds/pkg/Icons/24x24/Error';
import {
  ProfileOwnerWatchButton,
  ProfileOwnerSubscribeButton,
} from '@wix/da-shared-react/pkg/Button';
import IconButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import BaseSubNavBarDesktop, {
  Props as BaseProps,
} from '../../BaseSubNavBarDesktopInner';
import WatchButtonConnectContainer from '../../../WatchButton.container';
import RightSideButtonWrapper from '../../BaseSubNavBarDesktopInner/legos/RightSideButtonWrapper';
import LargeButtonsWrapper from '../../BaseSubNavBarDesktopInner/legos/LargeButtonsWrapper';
import ProfileSkinsButton from '../_partials/ProfileSkinsButton';
import GiveButton from '@wix/da-shared-react/pkg/gifting/GiveButton';
import { useGiftMenuItems } from '@wix/da-shared-react/pkg/gifting/giftMenuItems';
import GiftingReduxModule from '@wix/da-shared-react/pkg/gifting/redux/ModuleLoaderLazy';
import { useCollapsingRoutes } from '../../useCollapsingRoutes';
import { CollapsingElementsContainer } from '../../CollapsingElements';
import { CollapsingElementId } from '../../CollapsingElements/types';
import NoteButton from './_partials/NoteButton';
import SendMessageButton from './_partials/SendMessageButton';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { ModalType } from '../../../../../../types/modals';
import { useCopyProfileLinkClick } from '../../../../../utils/useCopyProfileLinkClick';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import s from './UserProfileSubNavDesktopInner.scss';

const WatchButton = WatchButtonConnectContainer(ProfileOwnerWatchButton);

export interface Props extends BaseProps {
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  isLoggedIn: boolean;
  hasSubscriptions: boolean;
  isAuthorizedToCustomize: boolean;
  isProfileOwnerWatchingCurrentUser: boolean;
  hasPublicBirthday?: boolean;
  isContactable?: boolean;
  blockProfileOwner: () => void;
}

enum COLLAPSING_BUTTON {
  MESSAGE = 'message',
  SHARE = 'share',
  GIVE = 'give',
  SUBSCRIPTIONS = 'subscriptions',
}

const UserProfileSubNavDesktopInner: FC<Props & WithVerificationProps> = ({
  isSticky,
  profileOwnerUser,
  isOwner,
  isLoggedIn,
  isAuthorizedToCustomize,
  isProfileOwnerWatchingCurrentUser,
  isVerificationNeeded,
  pushVerificationPopup,
  blockProfileOwner,
  hasPublicBirthday,
  hasSubscriptions,
  isContactable = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const damzFlag = useFlag('damz');
  const { routesToCollapse } = useCollapsingRoutes();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const [collapsedIds, setCollapsedIds] = useState<CollapsingElementId[]>([]);
  const messageButtonRef = useRef<HTMLDivElement | null>(null);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const giveButtonRef = useRef<HTMLDivElement | null>(null);
  const subscriptionsButtonRef = useRef<HTMLDivElement | null>(null);
  const giftMenuItems = useGiftMenuItems(profileOwnerUser);

  const { onCopyLinkClick } = useCopyProfileLinkClick(profileOwnerUser);

  const showNewSubscribeButton = !isOwner && hasSubscriptions;

  const hideWatchButton = profileOwnerUser.isSubscribed;
  const hideGiveButton = isSticky && hasSubscriptions;
  const isGiveButtonCollapsed =
    collapsedIds.includes(COLLAPSING_BUTTON.GIVE) || hideGiveButton;

  const collapsingButtons = [
    {
      id: COLLAPSING_BUTTON.MESSAGE,
      ref: messageButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.SHARE,
      ref: shareButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.GIVE,
      ref: giveButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.SUBSCRIPTIONS,
      ref: subscriptionsButtonRef,
    },
  ];

  const collapsedButtonsAsMenuItems = {
    [COLLAPSING_BUTTON.MESSAGE]: {
      icon: <MailIcon />,
      label: t(`navigation.subnavigation.sendNote`),
      onClick: isVerificationNeeded
        ? () => pushVerificationPopup('notes')
        : undefined,
      link: isVerificationNeeded
        ? undefined
        : Url.userNoteLink(profileOwnerUser.username),
    },
    [COLLAPSING_BUTTON.SHARE]: {
      icon: <ShareIcon />,
      label: t(`navigation.subnavigation.copyLink`),
      onClick: onCopyLinkClick,
    },
    [COLLAPSING_BUTTON.SUBSCRIPTIONS]: {
      icon: <TierIcon />,
      label: t(`navigation.route.var.subscriptions`),
      link: Url.userLink(
        profileOwnerUser.username,
        `${USER_PROFILE_SECTION_PATHS.SUBSCRIPTIONS}#tiers`
      ),
    },
  };

  const renderWatchButton = () => {
    return (
      <WatchButton
        className={s['watch-button']}
        isWatchingBack={isProfileOwnerWatchingCurrentUser}
      />
    );
  };

  const getMoreActionsMenuItems = () => {
    const menuItems: Array<NullableDropdownItem> = [];

    if (collapsedIds.length) {
      const collapsedIntoMenuItems = collapsedIds.map(
        id => collapsedButtonsAsMenuItems[id]
      );
      menuItems.push(...collapsedIntoMenuItems);
    }

    // add Give items to dropdown menu if:
    // 1) Give was collapsed due to window width
    // 2) give button is being hidden (due to having subscription), go ahead and add it to the dropdown menu
    if (isGiveButtonCollapsed) {
      menuItems.push({ items: giftMenuItems });
    }

    // Any collapsed routes are in a group
    if (routesToCollapse.length) {
      const routesMenuGroup: NullableDropdownItem = { items: [] };

      if (routesToCollapse.includes('posts')) {
        routesMenuGroup.items.push({
          icon: <QuillIcon />,
          label: t('navigation.subnavigation.posts'),
          link: Url.userLink(profileOwnerUser.username, 'posts'),
        });
      }

      if (routesToCollapse.includes('about')) {
        routesMenuGroup.items.push({
          icon: <InfoIcon />,
          label: t('navigation.subnavigation.about'),
          link: Url.userLink(profileOwnerUser.username, 'about'),
        });
      }

      menuItems.push(routesMenuGroup);
    }

    // Share and Block are in the same menu group
    const shareBlockMenuGroup: NullableDropdownItem = { items: [] };

    shareBlockMenuGroup.items.push({
      icon: <ShareIcon />,
      label: t(`navigation.subnavigation.copyLink`),
      onClick: onCopyLinkClick,
    });

    if (isLoggedIn && !isOwner) {
      shareBlockMenuGroup.items.push({
        icon: <BlockIcon />,
        label: t('navigation.subnavigation.blockUser'),
        onClick: blockProfileOwner,
      });
    }

    shareBlockMenuGroup.items.push({
      icon: <ErrorIcon />,
      label: t('navigation.subnavigation.reportUser'),
      onClick: restrictToLoggedIn(ReferralType.REPORT, () => {
        dispatch(
          pushModal(ModalType.REPORT_PROFILE, {
            wrapInModal: false,
            params: { userId: profileOwnerUser.userId },
          })
        );
      }),
    });

    menuItems.push(shareBlockMenuGroup);

    return menuItems;
  };

  const renderMoreActionsIconDropdown = () => {
    const menuItems = getMoreActionsMenuItems();
    if (!menuItems.length) {
      return null;
    }

    return (
      <RightSideButtonWrapper>
        <IconButtonWithContextMenu
          items={menuItems}
          icon={EllipsisIcon}
          size="large"
          aria-label={t('common.more')}
        />
        {isGiveButtonCollapsed && (
          <Suspense fallback={null}>
            <GiftingReduxModule />
          </Suspense>
        )}
      </RightSideButtonWrapper>
    );
  };

  const rightSideContent = isOwner ? (
    isAuthorizedToCustomize && (
      <RightSideButtonWrapper>
        <ProfileSkinsButton />
      </RightSideButtonWrapper>
    )
  ) : (
    <>
      {renderMoreActionsIconDropdown()}

      {!hideGiveButton && (
        <RightSideButtonWrapper
          innerRef={giveButtonRef}
          isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.GIVE)}
        >
          <GiveButton
            user={profileOwnerUser}
            allowBirthdayAnimation={hasPublicBirthday}
          />
        </RightSideButtonWrapper>
      )}
      <RightSideButtonWrapper
        innerRef={messageButtonRef}
        isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.MESSAGE)}
      >
        {damzFlag ? (
          <SendMessageButton
            profileOwner={profileOwnerUser}
            isContactable={isContactable}
            iconOnly={true}
          />
        ) : (
          <NoteButton profileOwner={profileOwnerUser} />
        )}
      </RightSideButtonWrapper>

      <LargeButtonsWrapper>
        {!hideWatchButton && (
          <RightSideButtonWrapper>{renderWatchButton()}</RightSideButtonWrapper>
        )}
        {showNewSubscribeButton && (
          <RightSideButtonWrapper
            innerRef={subscriptionsButtonRef}
            isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.SUBSCRIPTIONS)}
          >
            <ProfileOwnerSubscribeButton user={profileOwnerUser} />
          </RightSideButtonWrapper>
        )}
      </LargeButtonsWrapper>
    </>
  );
  return (
    <BaseSubNavBarDesktop
      isSticky={isSticky}
      rightSideContent={
        <CollapsingElementsContainer
          collapsingElements={collapsingButtons}
          onCollapsedIdsChange={setCollapsedIds}
          justifyContent="flex-end"
        >
          {rightSideContent}
        </CollapsingElementsContainer>
      }
    />
  );
};
UserProfileSubNavDesktopInner.displayName = 'UserProfileSubNavDesktopInner';

export default withVerification(UserProfileSubNavDesktopInner);
